/**
 * Property Store — Replaces $rootScope.G_WORKORDER / G_PROPERTY bridge
 *
 * This Zustand store is the single source of truth for the current work order
 * and property context in React. It implements a dual-write bridge pattern:
 *
 * 1. HYDRATION: On mount (embedded mode), reads initial values from AngularJS
 *    $rootScope and subscribes to changes via polling.
 * 2. DUAL-WRITE: When React updates state, it also writes back to $rootScope
 *    so the AngularJS sidebar stays in sync.
 * 3. STANDALONE: In standalone mode, the store is self-sufficient.
 *
 * Migration path: Replace `getRootScopeProperty<WorkOrder>('G_WORKORDER')`
 * with `usePropertyStore(s => s.workOrder)` in each file.
 */

import { create } from 'zustand';
import { getRootScopeProperty, setRootScopeProperty } from '@/lib/legacyContext';
import { getMountConfig } from '@/mountConfig';

// ── Shared Types ─────────────────────────────────────────────────────────────
// These cover ALL fields used across the 83+ consumer files.
// Individual routes access subsets via optional chaining.

export interface WorkOrder {
  id?: number | string;
  orderNumber?: number | string;
  propertyLoanId?: number;
  department?: string;
  workCode?: string;
  billTo?: string;
  sourceSystem?: string;
  resultsScriptId?: number;
  showAllResults?: boolean;
  latestVendorUpdateURL?: string;
  isReadOnly?: boolean;
  cancellationDate?: string | null;
  workflowStateTransitionId?: number;
  workflowStateTransition?: {
    currentState?: {
      id?: number;
      code?: string;
      name?: string;
      editPermission?: string;
    };
    currentStateId?: number;
    displayStatus?: string;
  };
}

export interface GProperty {
  id?: number;
  spiPropertyId?: number;
  propertyLoanId?: number;
  loanNumber?: string;
  loanType?: string;
  address1?: string;
  city?: string;
  state?: string;
  zip?: string;
  investor?: string;
  active?: string;
  ctdV2Supported?: string;
  saleDate?: string;
  ftvDate?: string;
  secureDate?: string;
  conveyDate?: string;
  occupancy?: string;
  clientCode?: { code?: string };
}

// ── Store Interface ──────────────────────────────────────────────────────────

interface PropertyStore {
  // State
  workOrder: WorkOrder | null;
  property: GProperty | null;
  /** Image counts for the current work order (G_WORKORDER_IMAGE_COUNTS) */
  imageCounts: Record<string, number> | null;

  // Actions
  setWorkOrder: (wo: WorkOrder | null) => void;
  setProperty: (prop: GProperty | null) => void;
  setImageCounts: (counts: Record<string, number> | null) => void;
  clearAll: () => void;

  // Hydration from AngularJS (call once on mount in embedded mode)
  hydrateFromLegacy: () => void;
}

// ── Helper: sync to AngularJS $rootScope ─────────────────────────────────────

function syncToLegacy(key: string, value: unknown) {
  try {
    const { mode } = getMountConfig();
    if (mode === 'embedded') {
      setRootScopeProperty(key, value);
    }
  } catch {
    // Silently ignore — AngularJS may not be available
  }
}

// ── Flash messages bridge ────────────────────────────────────────────────────
// Mirrors propertySelectedService.js:351-357 — after $rootScope.G_PROPERTY is
// set, fetch the property's luggage and feed it to flashMessageService so the
// staff-only "Property Alerts" toastr warning fires (e.g. "Convey Date:
// 04/21/2026 — Property Has Been Conveyed"). Embedded mode is skipped because
// the AngularJS service still runs there and we'd double-toast otherwise.

function maybeFireFlashMessages(prop: GProperty | null, prevId: number | null | undefined) {
  if (!prop?.id) return;
  // Same property re-set (e.g. workOrder navigation that re-applies the same
  // GProperty) — don't re-fire the toast. Matches legacy intent: rules evaluate
  // when a NEW property is loaded.
  if (prevId != null && prevId === prop.id) return;

  try {
    const { mode } = getMountConfig();
    if (mode === 'embedded') return; // AngularJS propertySelectedService handles this
  } catch {
    // No mount config (test/SSR) — bail to be safe.
    return;
  }

  // Lazy imports so propertyStore stays free of the API + service deps in the
  // initial chunk (and to avoid any circular import risk with stores that
  // flashMessageService might pull in for toastr fallback).
  (async () => {
    try {
      const flashMod = await import('@/lib/flashMessageService');
      if (!flashMod.getFlashMessageRules()?.length) {
        // Rules haven't loaded yet (non-staff users never load them, so this is
        // also the SGStaff gate). Skip — there's nothing to evaluate.
        return;
      }
      const { getLuggage } = await import('@/features/coreScript/coreScriptApi');
      const questions = await getLuggage(prop.id as number);
      // getLuggage already returns the `.questions` payload (see
      // coreScriptApi.ts:28-31). Wrap it back into the shape
      // displayFlashMessages expects: { questions: Record<string, ...> }.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      flashMod.displayFlashMessages({ questions: questions as any });
    } catch {
      // Network / cache / decompression errors are non-fatal — the toast is a
      // staff-only UX nicety, not a gating signal.
    }
  })();
}

// ── Store ────────────────────────────────────────────────────────────────────

export const usePropertyStore = create<PropertyStore>((set) => ({
  workOrder: null,
  property: null,
  imageCounts: null,

  setWorkOrder: (wo) => {
    // Preserve runtime cache fields (e.g. `.bids` written by BidsSummaryRoute for
    // BidDispositionRoute.findNextPage) when the workOrder is refetched for the
    // same order number. AngularJS kept the same $rootScope.G_WORKORDER object
    // across routes, so grafted properties survived. React replaces the object
    // on every refetch, which loses these caches unless we re-graft them.
    set((state) => {
      const prev = state.workOrder;
      const next = wo;
      if (next && prev && prev.orderNumber === next.orderNumber) {
        const prevBids = (prev as WorkOrder & { bids?: unknown }).bids;
        const nextWithBids = next as WorkOrder & { bids?: unknown };
        if (prevBids && !nextWithBids.bids) {
          nextWithBids.bids = prevBids;
        }
      }
      return { workOrder: next };
    });
    syncToLegacy('G_WORKORDER', wo);
  },

  setProperty: (prop) => {
    // Capture previous id before we overwrite — used by the flash-messages
    // trigger below to detect a genuine property change vs a re-set of the
    // same property (which shouldn't re-fire the Property Alerts toast).
    const prevId = (() => {
      try {
        return usePropertyStore.getState().property?.id ?? null;
      } catch {
        return null;
      }
    })();
    set({ property: prop });
    syncToLegacy('G_PROPERTY', prop);
    maybeFireFlashMessages(prop, prevId);
  },

  setImageCounts: (counts) => {
    set({ imageCounts: counts });
    syncToLegacy('G_WORKORDER_IMAGE_COUNTS', counts);
  },

  clearAll: () => {
    set({ workOrder: null, property: null, imageCounts: null });
    syncToLegacy('G_WORKORDER', null);
    syncToLegacy('G_PROPERTY', null);
    syncToLegacy('G_WORKORDER_IMAGE_COUNTS', null);
  },

  hydrateFromLegacy: () => {
    // Embedded-only: $rootScope is the authoritative source. In standalone mode,
    // `_standaloneScopeStore` is never written (syncToLegacy skips writes), so
    // calling this would overwrite the Zustand store (the actual source of truth
    // in standalone) with empty/null values — a regression after every
    // spiSearchCallBack event in the dashboard's listener.
    const { mode } = getMountConfig();
    if (mode !== 'embedded') return;

    const wo = getRootScopeProperty<WorkOrder>('G_WORKORDER');
    const prop = getRootScopeProperty<GProperty>('G_PROPERTY');
    const counts = getRootScopeProperty<Record<string, number>>('G_WORKORDER_IMAGE_COUNTS');
    set({
      workOrder: wo && Object.keys(wo).length > 0 ? wo : null,
      property: prop && Object.keys(prop).length > 0 ? prop : null,
      imageCounts: counts && Object.keys(counts).length > 0 ? counts : null,
    });
  },
}));

// ── Convenience Selectors ────────────────────────────────────────────────────
// Use these for simple reads: `const wo = useWorkOrder();`

export function useWorkOrder(): WorkOrder | null {
  return usePropertyStore((s) => s.workOrder);
}

export function useProperty(): GProperty | null {
  return usePropertyStore((s) => s.property);
}

export function useOrderNumber(): number | string | undefined {
  return usePropertyStore((s) => s.workOrder?.orderNumber);
}

export function usePropertyLoanId(): number | undefined {
  return usePropertyStore((s) => s.property?.id ?? s.workOrder?.propertyLoanId);
}

export function useWorkOrderImageCounts(): Record<string, number> | null {
  return usePropertyStore((s) => s.imageCounts);
}
